import { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { FlowForm, useFlowFormGlobal, FlowFormLeaveContext, get2DKeyHandler, getKeyHandler, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { ShortcutsContext, ShortcutGroup } from '../components/ShortcutsOverlay';
import { READER_KEYS_USED_KEY } from './Main';
import { Spinner, TextButton, FocusText } from 't-lib-ui/widgets';
import { RouterContext, goTo } from 't-lib-ui/router';
import { QueryStatus } from 't-lib-ui';
import MainLayout from '../components/MainLayout';
import { requestStartCollapsed } from '../components/ThreeColumnLayout';
import PageHeader from '../components/PageHeader';
import FeedQuotaDialog from '../components/FeedQuotaDialog';
import InterestsIcon from '../components/InterestsIcon';
import ImportIcon from '../components/ImportIcon';
import SearchIcon from '../components/SearchIcon';
import ChevronLeftIcon from '../components/ChevronLeftIcon';
import NewPostsIcon from '../components/NewPostsIcon';
import DiscoverIcon from '../components/DiscoverIcon';
import RandomIcon from '../components/RandomIcon';
import FeedIcon from '../components/FeedIcon';
import FolderIcon from '../components/FolderIcon';
import QuestionMarkIcon from '../components/QuestionMarkIcon';
import MailIcon from '../components/MailIcon';
import UserIcon from '../components/UserIcon';
import { useShowMeWhere, ShowMeWhereGuide } from '../components/ShowMeWhere';
import AllReadIllustration from '../components/AllReadIllustration';
import RssBankruptcyCard, { bankruptcyConditionsMet } from '../components/RssBankruptcyCard';
import HeaderAuthButtons from '../components/HeaderAuthButtons';
import LegalFooter from '../components/LegalFooter';
import PreviewFeedPopup from '../components/PreviewFeedPopup';
import FeedQuotaCard from '../components/FeedQuotaCard';
import { usePreferences } from '../connector/preferences';
import { getLastActiveMs } from '../connector/lastActive';
import { getLastMarkReadMs } from '../connector/lastMarkRead';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import { AppState } from '../context/general';
import { getMode } from '../connector/auth';
import { useGetDiscoverOnboarding, getPublicFeed } from '../connector/api';
import { flushPendingPreferences } from '../connector/preferences';
import { useEngineState } from '../utils/useEngineState';
import FeedLoadError from '../components/FeedLoadError';
import { MAX_ANON_FEEDS } from '../connector/feed-engine';
import { cameFromShare } from '../connector/anon-onboarding';
import { formatLastPostTime, formatCategory, formatStatCount } from '../utils/formatting';
import type { RestUserFeed, RestCatalog } from '../schema-copy/api-schema';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import './HomePage.css';

// Distinct per-card accent colors, cycled deterministically by card index.
const CARD_PALETTE = [
  '#1976D2', '#2E7D32', '#C2185B', '#7B1FA2',
  '#F57C00', '#00838F', '#5D4037', '#455A64',
  '#D32F2F', '#0288D1',
];

// Stable "semi-random" folder accent: hash the folder name to a hue; fixed
// saturation/lightness keep every folder readable in both themes.
export const folderColor = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 62%, 43%)`;
};

type Choice = 'none' | 'little' | 'full';

const HOME_SHORTCUTS: ShortcutGroup[] = [
  { title: 'Home', shortcuts: [
    { keys: ['j'], description: 'Open first feed with new posts' },
    { keys: ['r'], description: 'Open Read later list' },
  ] },
];

// Single-letter code appended to UserPreferences.dismissed once the keyboard-tips card is closed.
const KBD_CARD_DISMISS = 'k';
// Likewise for the one-time "Need any help?" card.
const HELP_CARD_DISMISS = 'h';

// "Pick your interests" never selects more than this many feeds in total (already-followed + newly
// picked), so a few "A lot" topics can't blow past the feed quota. Kept comfortably below the 30
// anonymous cap so the user still has room to add more by hand afterwards.
const TOPIC_FEED_CAP = 25;

const KeyboardIcon: React.FC<{ size?: number }> = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <path d="M6 10h.01M9.5 10h.01M13 10h.01M16.5 10h.01M6 13.5h.01M16.5 13.5h.01" />
    <path d="M9.2 13.5h5.6" />
  </svg>
);

// Arrow-into-door — log in to an existing account.
const LoginIcon: React.FC<{ size?: number }> = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 17l5-5-5-5" />
    <path d="M15 12H3" />
    <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
  </svg>
);

// Stylized italic "GO" — the zero-friction starting point.
const GoIcon: React.FC<{ size?: number }> = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <text x="12" y="12" textAnchor="middle" dominantBaseline="central"
      fontFamily="inherit" fontSize="11.5" fontWeight="800" fontStyle="italic" letterSpacing="-0.5">GO</text>
  </svg>
);

// Only greet returning users with "Welcome back" if they've been away
// this long - seeing it right after you just left is irritating.
const WELCOME_BACK_AFTER_MS = 4 * 24 * 60 * 60 * 1000;
// If the last visit was longer ago than this, an empty unread list means
// "nothing new arrived yet" rather than "you just read everything".
const CAUGHT_UP_STALE_MS = 3 * 60 * 60 * 1000;
// If the user marked something read this recently, an empty unread list plainly means
// "you just caught up" — so we drop the "nothing new arrived" preamble and shorten the
// headline to "All read." instead.
const JUST_CAUGHT_UP_MS = 3 * 60 * 1000;

const HomePage: React.FC = () => {
  useFlowFormGlobal();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const { feedEngine } = appState;
  // Anonymous (no-login) visitors get a welcome tailored to first-time, possibly-unfamiliar users:
  // no OPML import, a nudge to log in, an explanation of what Downread is, and legal links.
  const anon = getMode() == 'anon';
  // Visitors who arrived by following a feed from a public Share/Subscribe link didn't come to
  // Downread because they heard about it — they came for that one feed. Greet them with the basics
  // (and the limits of anon mode) instead of the generic pitch. The flag is set on the share pages.
  const [fromShare] = useState(() => anon && cameFromShare());

  // Navigate to a full layout page; on mobile keep the sidebar drawer closed
  // so it doesn't open in front of the destination page.
  const navigate = (path: string) => {
    // Going to a feed in narrow/mobile opens straight into read mode (sidebar hidden);
    // the setup/discover destinations keep the normal layout.
    if (path == '/a/new' || path == '/a/starred' || path == '/a/read-later' || path == '/a/random' || path.startsWith('/a/feed/') || path.startsWith('/a/new/'))
      requestStartCollapsed();
    goTo(routerCtx, path);
  };

  const [preferences, updatePreferences] = usePreferences();
  const onboarding = useGetDiscoverOnboarding({ enabled: true });
  const collections = useMemo(
    () => [...(onboarding.data?.collections ?? [])].sort((a, b) => (b.priority ?? -Infinity) - (a.priority ?? -Infinity)),
    [onboarding.data]
  );

  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [showInterests, setShowInterests] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showQuotaDialog, setShowQuotaDialog] = useState(false);
  // "Latest feeds" preview: clicking a feed opens it embedded in a PreviewFeedPopup instead of
  // navigating to the Add page.
  const [previewFeed, setPreviewFeed] = useState<RestUserFeed | RestCatalog | null>(null);
  const previewLatestFeed = async (feedId: string) => {
    try {
      // getPublicFeed works for any public feed (with or without a catalog entry) and for both
      // logged-in and anonymous users; PreviewFeedPopup accepts RestUserFeed | RestCatalog.
      setPreviewFeed(await getPublicFeed(feedId, appState.state.authData));
    } catch (e) {
      console.error('Error loading latest feed preview:', e);
    }
  };
  // Collection ids whose feed list is currently revealed (curious users
  // want to see exactly what they'd subscribe to).
  const [revealedFeeds, setRevealedFeeds] = useState<Set<string>>(new Set());
  const toggleRevealed = (id: string) => setRevealedFeeds(prev => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  // Live snapshot of the user's library, for the vivid "returning user"
  // dashboard. All data is already in memory in the feed engine - no extra
  // network or DB requests (this page loads on every visit).
  const [feeds, setFeeds] = useState<RestUserFeed[]>(() => feedEngine.getFeeds());
  const [folders, setFolders] = useState(() => feedEngine.getFolders());
  const [counts, setCounts] = useState(() => ({ unread: 0, starred: feedEngine.getStarredCount(), readLater: feedEngine.getReadLaterCount() }));
  useEffect(() => {
    const recompute = () => {
      const fs = feedEngine.getFeeds();
      setFeeds([...fs]);
      setFolders(feedEngine.getFolders());
      setCounts({
        unread: fs.reduce((s, f) => s + feedEngine.getUnreadCount(f.id), 0),
        starred: feedEngine.getStarredCount(),
        readLater: feedEngine.getReadLaterCount(),
      });
    };
    const unF = feedEngine.onFeedsChange(recompute);
    const unU = feedEngine.onUnreadChange(recompute);
    const unS = feedEngine.onStarChange(recompute);
    recompute();
    return () => { unF(); unU(); unS(); };
  }, [feedEngine]);

  const unreadFeeds = useMemo(
    () => feeds
      .filter(f => feedEngine.getUnreadCount(f.id) > 0)
      // Unread-tracking ('u') feeds rank above no-tracking ('a') feeds; within each
      // group, most-recent first.
      .sort((a, b) => {
        const ra = feedEngine.getShowRead(a.id) == 'a' ? 1 : 0;
        const rb = feedEngine.getShowRead(b.id) == 'a' ? 1 : 0;
        if (ra != rb) return ra - rb;
        return (b.lastNewPostAtSemi ?? 0) - (a.lastNewPostAtSemi ?? 0);
      }),
    [feeds, feedEngine]
  );
  const recentFeeds = useMemo(() => unreadFeeds.slice(0, 8), [unreadFeeds]);

  // Folder grouping of the unread list. A folder gets a card when any of its feeds has
  // unread posts; unfiled unread feeds keep their individual cards. The folder view is
  // only offered when at least one folder actually groups more than one unread feed
  // (otherwise it adds nothing over the feed list).
  type HomeCard =
    | { kind: 'feed'; feed: RestUserFeed; browseOnly: boolean; recency: number }
    | { kind: 'folder'; name: string; members: RestUserFeed[]; unread: number; browseOnly: boolean; recency: number };
  const homeCards = useMemo<HomeCard[]>(() => {
    const unreadById = new Map(unreadFeeds.map(f => [f.id, f]));
    const namedFolders = Object.entries(folders).filter(([name]) => name != '');
    const inFolder = new Set(namedFolders.flatMap(([, ids]) => ids));
    const cards: HomeCard[] = namedFolders
      .map(([name, ids]) => {
        const members = ids.map(id => unreadById.get(id)).filter((f): f is RestUserFeed => !!f);
        return {
          kind: 'folder' as const,
          name,
          members,
          unread: members.reduce((s, f) => s + feedEngine.getUnreadCount(f.id), 0),
          browseOnly: members.every(f => feedEngine.getShowRead(f.id) == 'a'),
          recency: members.reduce((m, f) => Math.max(m, f.lastNewPostAtSemi ?? 0), 0),
        };
      })
      .filter(c => c.members.length);
    cards.push(...unreadFeeds
      .filter(f => !inFolder.has(f.id))
      .map(f => ({ kind: 'feed' as const, feed: f, browseOnly: feedEngine.getShowRead(f.id) == 'a', recency: f.lastNewPostAtSemi ?? 0 })));
    // Same ranking as the feed list: unread-tracking entries above browse-only, then most-recent first.
    cards.sort((a, b) => (a.browseOnly ? 1 : 0) - (b.browseOnly ? 1 : 0) || b.recency - a.recency);
    return cards;
  }, [unreadFeeds, folders, feedEngine]);
  const folderViewAvailable = useMemo(
    () => homeCards.some(c => c.kind == 'folder' && c.members.length > 1),
    [homeCards]);
  const folderMode = folderViewAvailable && preferences.homeGrouping == 'folders';
  const recentCards = useMemo(() => homeCards.slice(0, 8), [homeCards]);

  // 'j' opens the first feed with new posts. Kept in a ref so the key handler
  // registers once and always sees the current top-of-list feed.
  const firstUnreadRef = useRef<string | null>(null);
  firstUnreadRef.current = unreadFeeds[0]?.id ?? null;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // "Need any help?" card: the help link inside it points (via the ShowMeWhere guide) at the
  // question-mark button in the page header, which opens the help page.
  const questionRef = useRef<HTMLButtonElement>(null);
  const helpLinkRef = useRef<HTMLSpanElement>(null);
  const { trigger: triggerHelpWhere, guide: helpWhereGuide, flashClassName: helpWhereFlashClass } = useShowMeWhere(helpLinkRef, questionRef);

  // Anonymous users cap at MAX_ANON_FEEDS feeds. Once they hit the limit, surface a card prompting
  // sign-up for more. Dismissable for the session; reappears next visit while still at the limit.
  // Takes priority over the keyboard / help cards (only one card shows at a time).
  const atFeedQuota = anon && feeds.length >= MAX_ANON_FEEDS;
  const [quotaCardClosed, setQuotaCardClosed] = useState(false);
  const showQuotaCard = !showInterests && atFeedQuota && !quotaCardClosed;

  // One-time "keyboard user" tips card: shown to people who have navigated posts with
  // j/k/n/p (a per-device localStorage signal set in Main.tsx), until they dismiss it -
  // the dismissal is stored in the synced `dismissed` preference so it stays gone everywhere.
  const usedReaderKeys = useMemo(() => {
    try { return localStorage.getItem(READER_KEYS_USED_KEY) == '1'; } catch { return false; }
  }, []);
  const [kbdCardClosed, setKbdCardClosed] = useState(false);
  const showKbdCard = usedReaderKeys && !preferences.dismissed?.includes(KBD_CARD_DISMISS) && !kbdCardClosed && !showQuotaCard;
  const dismissKbdCard = () => {
    setKbdCardClosed(true);
    if (!preferences.dismissed?.includes(KBD_CARD_DISMISS))
      updatePreferences({ dismissed: (preferences.dismissed || '') + KBD_CARD_DISMISS });
  };
  const showKbdCardRef = useRef(showKbdCard);
  showKbdCardRef.current = showKbdCard;
  const dismissKbdCardRef = useRef(dismissKbdCard);
  dismissKbdCardRef.current = dismissKbdCard;

  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      const tag = el?.tagName.toLowerCase();
      return tag == 'input' || tag == 'textarea' || !!(el as HTMLElement | null)?.isContentEditable;
    };
    // Priority above the layout's Escape-collapses-sidebar handler so Esc closes the card
    // first when it's open; when it's not, we return false and Esc falls through as before.
    registerKeyHandler('home-page', {
      'j': () => {
        if (isTyping()) return false;
        const id = firstUnreadRef.current;
        if (!id) return false;
        navigateRef.current(`/a/feed/${id}`);
        return true;
      },
      'r': () => {
        if (isTyping()) return false;
        navigateRef.current('/a/read-later');
        return true;
      },
      'Escape': () => {
        if (!showKbdCardRef.current) return false;
        dismissKbdCardRef.current();
        return true;
      },
    }, false, 10);
    return () => unregisterKeyHandler('home-page');
  }, []);
  // Feeds/folders with unread posts that didn't fit in the 8 shown cards, summarized
  // in a single line between the cards and the "All unread posts" card.
  const overflow = useMemo(() => {
    if (folderMode) {
      const rest = homeCards.slice(8);
      return {
        feedCount: rest.filter(c => c.kind == 'feed').length,
        folderCount: rest.filter(c => c.kind == 'folder').length,
        postCount: rest.reduce((s, c) => s + (c.kind == 'folder' ? c.unread : feedEngine.getUnreadCount(c.feed.id)), 0),
      };
    }
    const rest = unreadFeeds.slice(8);
    return { feedCount: rest.length, folderCount: 0, postCount: rest.reduce((s, f) => s + feedEngine.getUnreadCount(f.id), 0) };
  }, [folderMode, homeCards, unreadFeeds, feedEngine]);

  // Feed IDs implied by the current selections, capped at TOPIC_FEED_CAP total feeds. Already-followed
  // feeds (from search, "Just try it", etc.) count toward the cap and are deduped out. When the
  // selection exceeds the budget, feeds are trimmed round-robin starting from the last (lowest
  // priority) category, so every chosen topic keeps a fair share and high-priority topics keep most.
  const { selectedFeedIds, includedByCol, capReached } = useMemo(() => {
    const existing = new Set(feeds.map(f => f.id));
    const budget = Math.max(0, TOPIC_FEED_CAP - existing.size);

    // Per-collection candidate feeds in priority order; dedup across topics + drop already-followed.
    const seen = new Set(existing);
    const cats: { id: string; feeds: string[] }[] = [];
    for (const col of collections) {
      const c = choices[col.id] ?? 'none';
      const ids = c == 'little' ? [col.baseFeed.feedId] : c == 'full' ? col.feeds.map(f => f.feedId) : [];
      const fresh = ids.filter(id => !seen.has(id));
      fresh.forEach(id => seen.add(id));
      if (fresh.length) cats.push({ id: col.id, feeds: fresh });
    }

    let total = cats.reduce((s, c) => s + c.feeds.length, 0);
    const capReached = total > budget;
    // Round-robin removal from the last category backward until within budget.
    let i = cats.length - 1;
    let guard = cats.length * 1000 + 1;
    while (total > budget && total > 0 && guard-- > 0) {
      if (cats[i].feeds.length) { cats[i].feeds.pop(); total--; }
      i = i <= 0 ? cats.length - 1 : i - 1;
    }

    const includedByCol: Record<string, Set<string>> = {};
    for (const c of cats) includedByCol[c.id] = new Set(c.feeds);
    return { selectedFeedIds: cats.flatMap(c => c.feeds), includedByCol, capReached };
  }, [collections, choices, feeds]);

  const handleComplete = async () => {
    if (!feedEngine || completing) return;
    const subscribed = appState.state.currentUser?.feeds ?? [];
    const toAdd = selectedFeedIds.filter(id => !subscribed.includes(id));
    setCompleting(true);
    try {
      await flushPendingPreferences();
      const added = (await Promise.all(toAdd.map(id => feedEngine.subscribe(id)))).filter((f): f is RestUserFeed => !!f);
      // Configure auto-picked feeds up-front like the user chose "Track unread counts": track unread
      // (showRead 'u') AND set a real sort order — the "How do you want to read this feed?" card only
      // shows while sortOrder is the unconfigured 'A', so this suppresses it for every onboarding feed.
      added.forEach(f => { feedEngine.setShowRead(f.id, 'u'); feedEngine.setSortOrder(f.id, 'N'); });
      await Promise.all(added.map(f => feedEngine.applyStarterReadLimit(f.id)));
      // Parallel subscribe resolves in arbitrary order; restore the onboarding priority order.
      await feedEngine.moveFeeds(added.map(f => f.id), null);
      // Land on Home, not the unread list — the user just bulk-subscribed and doesn't yet know which
      // feeds they got; the Home dashboard shows them. We're already on the Home page, so close the
      // interests view and let the now-populated library flip it to the returning-user dashboard.
      setShowInterests(false);
      setCompleting(false);
      navigate('/a/home');
    } catch (error) {
      if (error && typeof error == 'object' && 'type' in error && (error as any).type == 'Quota')
        setShowQuotaDialog(true);
      else
        console.error('Onboarding subscribe failed:', error);
      setCompleting(false);
    }
  };

  // "Just try it": no decisions - subscribe to a ready-made starter set (one essential feed from
  // each of the top topics) and drop straight into the unread list. Falls back to Discover if the
  // onboarding catalog hasn't loaded yet.
  const handleTryIt = async () => {
    if (!feedEngine || completing) return;
    const starterIds = Array.from(new Set(collections.slice(0, 8).map(c => c.baseFeed.feedId)));
    if (!starterIds.length) { navigate('/a/discover'); return; }
    setCompleting(true);
    try {
      await flushPendingPreferences();
      const added = (await Promise.all(starterIds.map(id => feedEngine.subscribe(id)))).filter((f): f is RestUserFeed => !!f);
      // Configure auto-picked feeds up-front like the user chose "Track unread counts": track unread
      // (showRead 'u') AND set a real sort order — the "How do you want to read this feed?" card only
      // shows while sortOrder is the unconfigured 'A', so this suppresses it for every onboarding feed.
      added.forEach(f => { feedEngine.setShowRead(f.id, 'u'); feedEngine.setSortOrder(f.id, 'N'); });
      await Promise.all(added.map(f => feedEngine.applyStarterReadLimit(f.id)));
      // Parallel subscribe resolves in arbitrary order; restore the onboarding priority order (the
      // starter ids are already sorted by collection priority).
      await feedEngine.moveFeeds(added.map(f => f.id), null);
      // Land on Home, not the unread list — the user just bulk-subscribed and doesn't yet know which
      // feeds they got; the now-populated library flips Home to the returning-user dashboard.
      setCompleting(false);
      navigate('/a/home');
    } catch (error) {
      if (error && typeof error == 'object' && 'type' in error && (error as any).type == 'Quota')
        setShowQuotaDialog(true);
      else
        console.error('Try-it subscribe failed:', error);
      setCompleting(false);
    }
  };

  const skip = () => navigate((appState.state.currentUser?.feeds?.length ?? 0) > 1 ? '/a/new' : '/a/discover');

  // 2D spatial navigation so arrow keys move across the multi-column grids
  // (option cards, "Jump back in", latest feeds, topic cards) row/column-wise.
  const optionCardKeyHandler = get2DKeyHandler((el: any) => el.click()) as any;

  // Two home modes, triggered by whether the user already has a real
  // library (>1 feed) - the same threshold the dashboard sections use.
  // Returning users get a slim welcome-back hero instead of the setup
  // flow (the dashboard below already gives them their actions).
  const returning = feeds.length > 1;
  // Reword the welcome hero when the user comes back to a big backlog.
  const bankruptcy = bankruptcyConditionsMet(feedEngine, preferences);

  // From the user's previous activity (captured at app start, before App stamps
  // "now" - see connector/lastActive): whether to greet a returning user with
  // "Welcome back", and whether an empty unread list should read as "nothing new
  // yet". Measured from real app usage, not just Home visits. 0 => treat as long ago.
  const [{ showWelcomeBack, postsLikelyStale }] = useState(() => {
    const prev = getLastActiveMs();
    const elapsed = prev ? Date.now() - prev : Infinity;
    return {
      // No prior-activity record means this is a first-ever load (e.g. a visitor who just signed up or
      // hit "Just try it" and bulk-subscribed) — a brand-new user, not a returning one — so never greet
      // them with "Welcome back", even though `elapsed` reads as Infinity.
      showWelcomeBack: !!prev && elapsed >= WELCOME_BACK_AFTER_MS,
      postsLikelyStale: elapsed > CAUGHT_UP_STALE_MS,
    };
  });

  // Until the engine has actually loaded the feed list (OFF/LOADING) we
  // can't tell new from returning users. Render a spinner instead of
  // briefly flashing the new-user setup card (flicker on direct load).
  // ERROR is neither ready nor loading: the library exists but couldn't be
  // loaded, so the new-user welcome must NOT show — after a server error it
  // reads as "your feeds are gone". Render an error panel instead.
  const engineState = useEngineState(feedEngine);
  const engineFailed = engineState == 'ERROR';
  const engineReady = engineState != 'OFF' && engineState != 'LOADING' && !engineFailed;

  // One-time help card: only when the page is otherwise "calm" - no welcome/setup hero, no
  // RSS bankruptcy card and no keyboard-tips card competing for attention - and until dismissed.
  const [helpCardClosed, setHelpCardClosed] = useState(false);
  const showHelpCard = !showInterests && returning && !showWelcomeBack && !bankruptcy && !showKbdCard && !showQuotaCard
    && !helpCardClosed && !preferences.dismissed?.includes(HELP_CARD_DISMISS);
  const dismissHelpCard = () => {
    setHelpCardClosed(true);
    if (!preferences.dismissed?.includes(HELP_CARD_DISMISS))
      updatePreferences({ dismissed: (preferences.dismissed || '') + HELP_CARD_DISMISS });
  };

  // Empty unread list right after the user finished reading: read fresh (not captured at
  // load) so it reflects a mark-read that happened during this session.
  const justCaughtUp = counts.unread == 0 && Date.now() - getLastMarkReadMs() < JUST_CAUGHT_UP_MS;

  const interestsLoading = onboarding.status == QueryStatus.Loading || !onboarding.data;

  // One "New posts available" card for a feed — used by the feed view and, for
  // unfiled feeds, by the folder view.
  const renderFeedCard = (f: RestUserFeed) => {
    const unread = feedEngine.getUnreadCount(f.id);
    const browseOnly = feedEngine.getShowRead(f.id) == 'a';
    const isNewsletter = f.feedStatus == 'NEWSLETTER';
    const meta = formatLastPostTime(f.lastNewPostPubDate) || formatCategory(f.category) || '';
    return (
      <button
        key={f.id}
        type="button"
        tabIndex={0}
        className="homeRecentCard flowFocussable flow2d"
        onClick={() => navigate(`/a/feed/${f.id}`)}
        onKeyDown={optionCardKeyHandler}
      >
        <FeedIcon className="homeRecentIcon" src={getFeedIconUrl(f, 48)} size={22} circular={f.circularIcon} fallback={<span className="homeRecentIcon homeRecentIconFallback">📰</span>} />
        <span className="homeRecentText">
          <span className="homeRecentName">{f.shortTitle || f.title}</span>
          {isNewsletter
            ? <span className="homeRecentMeta homeRecentMetaNewsletter"><MailIcon size={12} color="currentColor" />Newsletter Inbox</span>
            : meta && <span className="homeRecentMeta">{meta}</span>}
        </span>
        {unread > 0 && (browseOnly
          ? <span className="homeRecentDot" />
          : <span className="homeRecentBadge">{unread > 99 ? '99+' : unread}</span>)}
      </button>
    );
  };

  // A folder card: colored folder icon and member summary. Clicking opens the folder's
  // unread view — or the feed itself when only one member feed has unread posts.
  const renderFolderCard = (c: Extract<HomeCard, { kind: 'folder' }>) => {
    // The badge counts only tracked ('u') feeds. When untracked ('a') feeds in the folder also have
    // unread posts, append '+' to signal "and more untracked posts" (e.g. '8+'); a folder whose only
    // unread posts are untracked shows a bare dot instead of a number.
    const trackedUnread = c.members.reduce((s, f) => s + (feedEngine.getShowRead(f.id) == 'u' ? feedEngine.getUnreadCount(f.id) : 0), 0);
    const hasUntrackedUnread = c.members.some(f => feedEngine.getShowRead(f.id) == 'a' && feedEngine.getUnreadCount(f.id) > 0);
    return (
      <button
        key={`folder:${c.name}`}
        type="button"
        tabIndex={0}
        className="homeRecentCard flowFocussable flow2d"
        onClick={() => navigate(c.members.length == 1 ? `/a/feed/${c.members[0].id}` : `/a/new/${encodeURIComponent(c.name)}`)}
        onKeyDown={optionCardKeyHandler}
      >
        <span className="homeRecentIcon homeFolderIcon" style={{ color: folderColor(c.name) }}><FolderIcon size={22} /></span>
        <span className="homeRecentText">
          <span className="homeRecentName">{c.name}</span>
          <span className="homeRecentMeta">{c.members.length} {c.members.length == 1 ? 'feed' : 'feeds'} with new posts</span>
        </span>
        {trackedUnread > 0
          ? <span className="homeRecentBadge">{trackedUnread > 99 ? '99+' : `${trackedUnread}${hasUntrackedUnread ? '+' : ''}`}</span>
          : hasUntrackedUnread ? <span className="homeRecentDot" /> : null}
      </button>
    );
  };

  return (
    <ShortcutsContext.Provider value={HOME_SHORTCUTS}>
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
      <MainLayout
        selectedFeed="home"
        right={({ onExpand }) => (
          <div className="homePage">
            <PageHeader
              title={!engineReady ? '' : returning ? 'Home' : (
                <>
                  <span className="homeTitleFull">Welcome to Downread</span>
                  <span className="homeTitleShort">Welcome</span>
                </>
              )}
              onMenu={onExpand}
              rightContent={
                // A FlowForm so the header buttons join keyboard navigation (they sit outside the
                // page's main FlowForm); arrow-up from the content reaches them, left/right cycles.
                <FlowForm className="homeHeaderForm">
                  <HeaderAuthButtons bare />
                  <button
                    ref={questionRef}
                    type="button"
                    className={`homeHelpButton flowFocussable sidewayFocus ${helpWhereFlashClass}`}
                    tabIndex={0}
                    aria-label="Help"
                    title="Help"
                    onClick={() => goTo(routerCtx, '/a/help')}
                    onKeyDown={getKeyHandler(() => goTo(routerCtx, '/a/help'))}
                  >
                    <QuestionMarkIcon size={22} />
                  </button>
                </FlowForm>
              }
            />
            <FlowForm main={true}>
              <div className="homeContent">
                {engineFailed ? (
                  <FeedLoadError />
                ) : !engineReady ? (
                  <div className="homeLoading"><Spinner spinning={true} /></div>
                ) : (<>
                {!showInterests && (!returning || showWelcomeBack) && (
                  <div className="homeHero">
                    {returning ? (
                      <>
                        <h1 className="homeHeroTitle">Welcome back.</h1>
                        <p className="homeHeroSubtitle">{bankruptcy
                          ? 'A lot of posts have piled up since you last visited.'
                          : 'Pick up where you left off, or explore something new.'}</p>
                      </>
                    ) : fromShare ? (
                      <>
                        <h1 className="homeHeroTitle">You're reading on Downread.</h1>
                        <p className="homeHeroSubtitle">
                          Downread is a free RSS reader: follow any blog, news site, or YouTube channel
                          and read every new post in one fast, uncluttered stream. Add more feeds below.<br/>
                          You're using it without an account, so your feeds live only in this browser
                          (they don't sync to other devices, and clearing your browser data removes them).
                          You can follow up to {MAX_ANON_FEEDS} feeds. <a href="/signup">Sign up free</a> any
                          time to keep them safe, follow more feeds and read them everywhere.
                        </p>
                      </>
                    ) : (
                      <>
                        <h1 className="homeHeroTitle">{anon ? "You're in. Pick a starting point." : "Let's set up your reading."}</h1>
                        <p className="homeHeroSubtitle">
                          {anon
                            ? "Downread is an RSS reader for the open web. Follow the blogs, news sites, and YouTube channels you love, and keep up with everything they post - all in one fast, uncluttered stream. No account needed to start."
                            : "Downread brings the sites you love in a single place. Pick how you'd like to begin. It only takes a moment."}
                        </p>
                      </>
                    )}
                  </div>
                )}

                {!showInterests && returning && !showQuotaCard && <RssBankruptcyCard context="home" />}

                {showQuotaCard && <FeedQuotaCard onClose={() => setQuotaCardClosed(true)} />}

                {!showInterests && showKbdCard && (
                  <div className="homeKbdCard">
                    <button
                      type="button"
                      className="homeKbdClose flowFocussable sidewayFocus"
                      tabIndex={0}
                      aria-label="Dismiss"
                      onClick={dismissKbdCard}
                      onKeyDown={getKeyHandler(dismissKbdCard)}
                    >&times;</button>
                    <div className="homeKbdHeader">
                      <span className="homeKbdIcon"><KeyboardIcon size={26} /></span>
                      <h2 className="homeKbdTitle">Hello, fellow keyboard user!</h2>
                    </div>
                    <ul className="homeKbdList">
                      <li>
                        Press <kbd className="homeKbd">?</kbd> on any page to see every
                        available keyboard shortcut.
                      </li>
                      <li>
                        Navigate through the whole app with the arrow keys and <kbd className="homeKbd">Return</kbd>.
                        Press <kbd className="homeKbd">↓</kbd> to get going.
                      </li>
                    </ul>
                    <div className="homeKbdFooter">
                      <FocusText className="homeKbdDismiss" focussable="flow" underline onClick={dismissKbdCard}>
                        Got it, don't show again
                      </FocusText>
                      <kbd className="homeKbd">Esc</kbd>
                    </div>
                  </div>
                )}

                {showHelpCard && (
                  <div className="homeHelpCard">
                    <button
                      type="button"
                      className="homeHelpClose flowFocussable sidewayFocus"
                      tabIndex={0}
                      aria-label="Dismiss"
                      onClick={dismissHelpCard}
                      onKeyDown={getKeyHandler(dismissHelpCard)}
                    >&times;</button>
                    <h2 className="homeHelpTitle">Need any help?</h2>
                    <p className="homeHelpText">
                      To understand how Downread works, come back to this Home page any time and click on{' '}
                      <span ref={helpLinkRef} className="homeHelpWhereWrap">
                        <FocusText className="homeHelpWhere" focusVisible={false} focussable="flow" onClick={triggerHelpWhere}>
                          the question mark<QuestionMarkIcon size={15} />
                        </FocusText>
                      </span>{' '}
                      in the top-right corner.
                    </p>
                    <div className="homeHelpFooter">
                      <FocusText className="homeHelpMore" focussable="flow" underline onClick={() => goTo(routerCtx, '/a/help')}>
                        Show me more
                      </FocusText>
                      <FocusText className="homeHelpDismiss" focussable="flow" underline onClick={dismissHelpCard}>
                        Dismiss
                      </FocusText>
                    </div>
                  </div>
                )}

                {!returning && (showInterests ? (
                  <button
                    type="button"
                    tabIndex={0}
                    className="homeBackButton flowFocussable flow2d"
                    onClick={() => setShowInterests(false)}
                    onKeyDown={optionCardKeyHandler}
                  >
                    <ChevronLeftIcon size={14} />
                    <span>Back</span>
                  </button>
                ) : (
                  <div className="homeOptions">
                    <button
                      type="button"
                      tabIndex={0}
                      className="homeOptionCard homeOptionPrimary flowFocussable flow2d"
                      onClick={handleTryIt}
                      onKeyDown={optionCardKeyHandler}
                    >
                      <span className="homeOptionIcon"><GoIcon size={26} /></span>
                      <span className="homeOptionText">
                        <span className="homeOptionTitle">Just try it</span>
                        <span className="homeOptionDesc">Not sure where to start? We'll pick a few good feeds so you can dive right in.</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      tabIndex={0}
                      className="homeOptionCard homeOptionInterests flowFocussable flow2d"
                      onClick={() => setShowInterests(true)}
                      onKeyDown={optionCardKeyHandler}
                    >
                      <span className="homeOptionIcon"><InterestsIcon size={26} /></span>
                      <span className="homeOptionText">
                        <span className="homeOptionTitle">Pick your interests</span>
                        <span className="homeOptionDesc">Pick a few topics and we'll build your feed instantly.</span>
                      </span>
                    </button>
                    {!anon && <button
                      type="button"
                      tabIndex={0}
                      className="homeOptionCard homeOptionImport flowFocussable flow2d"
                      onClick={() => navigate('/a/import')}
                      onKeyDown={optionCardKeyHandler}
                    >
                      <span className="homeOptionIcon"><ImportIcon size={26} /></span>
                      <span className="homeOptionText">
                        <span className="homeOptionTitle">Import from another reader</span>
                        <span className="homeOptionDesc">Bring your subscriptions over with an OPML file.</span>
                      </span>
                    </button>}
                    <button
                      type="button"
                      tabIndex={0}
                      className="homeOptionCard homeOptionSearch flowFocussable flow2d"
                      onClick={() => navigate('/a/add')}
                      onKeyDown={optionCardKeyHandler}
                    >
                      <span className="homeOptionIcon"><SearchIcon size={26} /></span>
                      <span className="homeOptionText">
                        <span className="homeOptionTitle">Search &amp; add feeds yourself</span>
                        <span className="homeOptionDesc">Already know what you want? Find any site or RSS feed.</span>
                      </span>
                    </button>
                    {/* Desktop-only extras (hidden <1200px via .homeOptionDesktopOnly): OPML import
                        and sign-up are choices for desktop users; on small screens only the three
                        core options show. Both import and sign-up lead to the sign-up screen —
                        imports require an account. */}
                    {anon && <button
                      type="button"
                      tabIndex={0}
                      className="homeOptionCard homeOptionLogin homeOptionDesktopOnly flowFocussable flow2d"
                      onClick={() => { window.location.href = '/login'; }}
                      onKeyDown={optionCardKeyHandler}
                    >
                      <span className="homeOptionIcon"><LoginIcon size={26} /></span>
                      <span className="homeOptionText">
                        <span className="homeOptionTitle">Log in</span>
                        <span className="homeOptionDesc">Already have an account? Sign in to sync your feeds across devices.</span>
                      </span>
                    </button>}
                    {anon && <button
                      type="button"
                      tabIndex={0}
                      className="homeOptionCard homeOptionImport homeOptionDesktopOnly flowFocussable flow2d"
                      onClick={() => { window.location.href = '/signup'; }}
                      onKeyDown={optionCardKeyHandler}
                    >
                      <span className="homeOptionIcon"><ImportIcon size={26} /></span>
                      <span className="homeOptionText">
                        <span className="homeOptionTitle">Import OPML</span>
                        <span className="homeOptionDesc">Bring your subscriptions over from another reader. Sign up first — imports require an account.</span>
                      </span>
                    </button>}
                    {anon && <button
                      type="button"
                      tabIndex={0}
                      className="homeOptionCard homeOptionSignup homeOptionDesktopOnly flowFocussable flow2d"
                      onClick={() => { window.location.href = '/signup'; }}
                      onKeyDown={optionCardKeyHandler}
                    >
                      <span className="homeOptionIcon"><UserIcon size={26} /></span>
                      <span className="homeOptionText">
                        <span className="homeOptionTitle">Sign up</span>
                        <span className="homeOptionDesc">Your data is stored securely on our servers, and you can add new feeds beyond the anonymous limit.</span>
                      </span>
                    </button>}
                  </div>
                ))}

                {!showInterests && !returning && anon && (
                  <div className="homeAnonFooter">
                    <p className="homeAnonNote">
                      New to Downread?{' '}
                      {/* flow2d so 'up' from the link lands on the option card straight above it,
                          not on the DOM-order-previous (last) card. */}
                      <FocusText className="homeAnonLink" focussable="flow2d" underline onClick={() => goTo(routerCtx, '/a/help/intro')}>
                        See how it works
                      </FocusText>.
                    </p>
                  </div>
                )}

                {!showInterests && feeds.length > 1 && counts.unread > 0 && (
                  <div className="homeDashboard">
                    <div className="homeRecent">
                      <div className="homeSectionHeader">
                        <h2 className="homeSectionTitle">New posts available</h2>
                        {folderViewAvailable && (
                          <FocusText
                            className="homeGroupToggle"
                            focussable="flow2d"
                            onClick={() => updatePreferences({ homeGrouping: folderMode ? 'feeds' : 'folders' })}
                          >
                            {folderMode ? 'Show feeds' : 'Show folders'}
                          </FocusText>
                        )}
                      </div>
                      <div className="homeRecentGrid">
                        {folderMode
                          ? recentCards.map(c => c.kind == 'folder' ? renderFolderCard(c) : renderFeedCard(c.feed))
                          : recentFeeds.map(renderFeedCard)}
                        {(overflow.feedCount > 0 || overflow.folderCount > 0) && (
                          <p className="homeRecentMore">
                            … and {overflow.postCount} {overflow.postCount == 1 ? 'post' : 'posts'} in {[
                              overflow.folderCount ? `${overflow.folderCount} other ${overflow.folderCount == 1 ? 'folder' : 'folders'}` : '',
                              overflow.feedCount ? `${overflow.feedCount} other ${overflow.feedCount == 1 ? 'feed' : 'feeds'}` : '',
                            ].filter(Boolean).join(' and ')}.
                          </p>
                        )}
                        {recentFeeds.length > 1 && (
                          <button type="button" tabIndex={0} className="homeRecentCard homeSpecialCard homeRecentCardFull flowFocussable flow2d" onClick={() => navigate('/a/new')} onKeyDown={optionCardKeyHandler}>
                            <span className="homeRecentIcon homeSpecialIcon"><NewPostsIcon size={20} /></span>
                            <span className="homeRecentText">
                              <span className="homeRecentName">All unread posts</span>
                              <span className="homeRecentMeta">Everything new across your feeds</span>
                            </span>
                            <span className="homeRecentBadge">{counts.unread > 999 ? '999+' : counts.unread}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {!showInterests && feeds.length > 1 && counts.unread == 0 && (
                  <div className="homeDashboard">
                    <div className="homeCaughtUp">
                      <AllReadIllustration size={170} />
                      <h2 className="homeCaughtUpHeadline">
                        {justCaughtUp ? 'All read.' : postsLikelyStale ? 'No new posts yet' : "You're all caught up!"}
                      </h2>
                      <p className="homeCaughtUpText">
                        {justCaughtUp
                          ? 'Check back later, or explore something new in the meantime.'
                          : postsLikelyStale
                          ? 'Nothing new has arrived in your feeds since your last visit. Check back later, or explore something new in the meantime.'
                          : 'There are no unread posts in any of your feeds right now. Check back later, or explore new feeds in the meantime.'}
                      </p>
                      {counts.readLater > 0 && (
                        <button
                          type="button"
                          tabIndex={0}
                          className="homeCaughtUpLink flowFocussable flow2d"
                          onClick={() => navigate('/a/read-later')}
                          onKeyDown={optionCardKeyHandler}
                        >
                          {counts.readLater} saved in Read Later →
                        </button>
                      )}
                    </div>
                    <div className="homePromoGrid">
                      <button
                        type="button"
                        tabIndex={0}
                        className="homeOptionCard flowFocussable flow2d"
                        onClick={() => navigate('/a/discover')}
                        onKeyDown={optionCardKeyHandler}
                      >
                        <span className="homeOptionIcon"><DiscoverIcon size={26} /></span>
                        <span className="homeOptionText">
                          <span className="homeOptionTitle">Discover feeds</span>
                          <span className="homeOptionDesc">Browse hand-picked feeds and curated collections.</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        tabIndex={0}
                        className="homeOptionCard flowFocussable flow2d"
                        onClick={() => navigate('/a/random')}
                        onKeyDown={optionCardKeyHandler}
                      >
                        <span className="homeOptionIcon"><RandomIcon size={26} /></span>
                        <span className="homeOptionText">
                          <span className="homeOptionTitle">Surprise me</span>
                          <span className="homeOptionDesc">A random feed mixing posts from popular feeds.</span>
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {!showInterests && returning && onboarding.data?.stats && (
                  <div className="homeGlobal">
                    <h2 className="homeSectionTitle">Downread stats</h2>
                    <div className="homeGlobalStats">
                      <div className="homeGlobalStat">
                        <span className="homeGlobalNum">{formatStatCount(onboarding.data.stats.feeds, 100)}</span>
                        <span className="homeGlobalLabel">feeds</span>
                      </div>
                      <div className="homeGlobalStat">
                        {/* Narrow columns get the abbreviated forms ("3.3M", "new/hour") - see
                            the container query in HomePage.css. */}
                        <span className="homeGlobalNum">
                          <span className="homeStatFull">{formatStatCount(onboarding.data.stats.posts, 1000)}</span>
                          <span className="homeStatShort">{formatStatCount(onboarding.data.stats.posts, 1000, true)}</span>
                        </span>
                        <span className="homeGlobalLabel">posts</span>
                      </div>
                      <div className="homeGlobalStat">
                        <span className="homeGlobalNum">{onboarding.data.stats.postsLastHour.toLocaleString()}</span>
                        <span className="homeGlobalLabel">
                          <span className="homeStatFull">new this hour</span>
                          <span className="homeStatShort">new/hour</span>
                        </span>
                      </div>
                    </div>
                    {!!onboarding.data.latestFeeds?.length && (
                      <div className="homeLatest">
                        <h2 className="homeSectionTitle homeSectionTitleTight">Latest feeds</h2>
                        <p className="homeSectionByline">New feeds recently added to Downread.</p>
                        <div className="homeLatestGrid">
                          {onboarding.data.latestFeeds.map(f => (
                            <button
                              key={f.feedId}
                              type="button"
                              tabIndex={0}
                              className="homeLatestCard flowFocussable flow2d"
                              onClick={() => previewLatestFeed(f.feedId)}
                              onKeyDown={optionCardKeyHandler}
                            >
                              <FeedIcon className="homeLatestIcon" src={f.iconUrl} size={20} circular={f.circularIcon} fallback={<span className="homeLatestIcon homeLatestIconFallback">📰</span>} />
                              <span className="homeLatestName">{f.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {showInterests && (
                  <div className="homeInterests">
                    {interestsLoading ? (
                      <div className="homeInterestsLoading"><Spinner spinning={true} /></div>
                    ) : collections.length == 0 ? (
                      <p className="homeInterestsEmpty">No topics available yet - try Search or Import instead.</p>
                    ) : (
                      <>
                        <p className="homeInterestsHelp">
                          How interested are you in each topic? We'll add some feed we think you'll like.
                        </p>
                        {capReached && (
                          <p className="homeInterestsCap">
                            We'll add up to {TOPIC_FEED_CAP} feeds to keep you within the limit — the highest-priority
                            topics keep the most. You can always follow more later.
                          </p>
                        )}
                        <div className="homeTopicsGrid">
                          {collections.map((col, index) => {
                            const choice = choices[col.id] ?? 'none';
                            const accent = CARD_PALETTE[index % CARD_PALETTE.length];
                            return (
                              <div
                                key={col.id}
                                className="homeTopicCard"
                                style={{ ['--card-accent' as any]: accent } as React.CSSProperties}
                              >
                                <h3 className="homeTopicTitle">{col.topic}</h3>
                                <div className="homeTopicChoices">
                                  {([
                                    ['none', 'None'],
                                    ['little', 'A little'],
                                    ['full', 'A lot'],
                                  ] as const).map(([opt, label]) => {
                                    const select = () => setChoices(prev => ({ ...prev, [col.id]: opt }));
                                    return (
                                      <button
                                        key={opt}
                                        type="button"
                                        tabIndex={0}
                                        className={`homeChoiceBtn flowFocussable flow2d${choice == opt ? ' active' : ''}`}
                                        onClick={select}
                                        onKeyDown={get2DKeyHandler(select) as any}
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div className="homeTopicMeta">
                                  <p className="homeTopicCaption">
                                    {(() => {
                                      const included = includedByCol[col.id]?.size ?? 0;
                                      if (choice == 'none') return 'Not following this topic';
                                      if (included == 0) return 'Feed limit reached';
                                      if (choice == 'little') return 'Just the essentials (1 feed)';
                                      return `${included} ${included == 1 ? 'feed' : 'feeds'}`;
                                    })()}
                                  </p>
                                  {(() => {
                                    const revealed = revealedFeeds.has(col.id);
                                    return (
                                      <button
                                        type="button"
                                        tabIndex={0}
                                        className="homeTopicReveal flowFocussable flow2d"
                                        aria-expanded={revealed}
                                        onClick={() => toggleRevealed(col.id)}
                                        onKeyDown={get2DKeyHandler(() => toggleRevealed(col.id)) as any}
                                      >
                                        {revealed ? 'Hide feeds' : 'Show feeds'}
                                      </button>
                                    );
                                  })()}
                                </div>
                                {revealedFeeds.has(col.id) && (() => {
                                  const feedList = col.feeds.some(f => f.feedId == col.baseFeed.feedId)
                                    ? col.feeds
                                    : [col.baseFeed, ...col.feeds];
                                  return (
                                    <ul className="homeFeedList">
                                      {feedList.map(f => (
                                        <li key={f.feedId} className="homeFeedItem">
                                          <FeedIcon className="homeFeedIcon" src={f.iconUrl} size={16} circular={f.circularIcon} />
                                          <a className="homeFeedName" href={f.siteUrl} target="_blank" rel="noopener noreferrer">{f.title}</a>
                                          {f.feedId == col.baseFeed.feedId && (
                                            <span className="homeFeedBase" title="Included with “A little”">essential</span>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {showInterests && (
                  <div className="homeCompleteBar">
                    <div className="homeCompleteAction">
                      {completing && <Spinner spinning={true} />}
                      <TextButton
                        className="homeAuthBtn"
                        label={completing ? 'Adding your feeds…' : 'Add my feeds & start reading'}
                        type="major"
                        enabled={completing ? 'disabled' : 'enabled'}
                        onClick={handleComplete}
                      />
                    </div>
                    <FocusText className="homeSkipLink" focussable="flow" underline={true} onClick={skip}>
                      Skip for now
                    </FocusText>
                  </div>
                )}

                <LegalFooter tight={!returning} />
                </>)}
              </div>
            </FlowForm>
            <PreviewFeedPopup
              show={!!previewFeed}
              feed={previewFeed}
              onClose={() => setPreviewFeed(null)}
              subscribed={previewFeed ? feeds.some(f => f.id == previewFeed.id) : false}
              onSubscribe={async () => {
                if (!previewFeed) return;
                try {
                  await flushPendingPreferences();
                  // Hand the engine the catalog metadata so the anonymous engine can subscribe
                  // without a server lookup (mirrors FeedTile).
                  feedEngine.registerFeedInfo(previewFeed);
                  const result = await feedEngine.subscribe(previewFeed.id);
                  if (result) setPreviewFeed(null);
                } catch (e) {
                  if (e && typeof e == 'object' && 'type' in e && (e as any).type == 'Quota') {
                    setPreviewFeed(null);
                    setShowQuotaDialog(true);
                  } else {
                    console.error('Follow from preview failed:', e);
                  }
                }
              }}
            />
            <FeedQuotaDialog show={showQuotaDialog} onClose={() => setShowQuotaDialog(false)} />
            <ShowMeWhereGuide guide={helpWhereGuide} />
          </div>
        )}
        startWithLeftCollapsed={typeof window !== 'undefined' && window.innerWidth <= 750}
      />
    </FlowFormLeaveContext.Provider>
    </ShortcutsContext.Provider>
  );
};

export default HomePage;
